import { type ReactNode, useContext } from "react";
import { Redirect } from "react-router";
import { ScreenMax } from "styles/antTheme/Variables";
import { useMediaQuery } from "usehooks-ts";

import { UserInfoContext } from "helpers/globalContext/UserInfoContext";

import { BTLoginTypes } from "types/enum";

import { routes } from "utilities/routes";

import { getSpecificationViewPreference } from "entity/specification/common/specificationViewPreference";

interface ISpecificationDefaultViewProps {
    /**
     * Renders the Book View. Called only when Book View is the landing view, so the book page's
     * lazy-loaded chunk is never mounted for users who land on the grid.
     */
    renderBookView: () => ReactNode;
}

/**
 * Persona branching for the base `/Specifications` route.
 *
 * Internal users (builders) land on the AG Grid so they start in the authoring surface; everyone
 * else — subs, clients/owners, external viewers — keeps the reading-friendly Book View. A builder
 * who has used the Book/Grid toggle gets whichever view they last chose instead.
 *
 * This is the single place that decision is made, so entry points into the Specifications area
 * should link to the base route rather than re-deriving the persona rules.
 */
export function SpecificationDefaultView({
    renderBookView,
}: Readonly<ISpecificationDefaultViewProps>) {
    const userInfo = useContext(UserInfoContext);
    // Matches the breakpoint SpecificationBook uses to switch to its stacked layout. The grid has
    // no mobile experience, so internal users on a small screen stay on Book View.
    const useMobileLayout = useMediaQuery(`(max-width: ${ScreenMax.md}px)`);

    const isBuilder = userInfo?.loginType === BTLoginTypes.BUILDER;
    // Read during render rather than through state: this component only ever redirects or renders
    // Book View, so seeding state would show Book View for a frame before bouncing to the grid.
    // Only an explicit "book" diverts from the grid, so an unset or unparseable value falls back
    // to the persona rule.
    const prefersBookView = getSpecificationViewPreference() === "book";

    if (isBuilder && !useMobileLayout && !prefersBookView) {
        return <Redirect to={routes.specification.getListLink()} />;
    }

    return <>{renderBookView()}</>;
}
